import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose, IconCheck } from './Icons';
import Agente456Anim from './Agente456Anim';
import { colore, etichetta, quando, conteggio } from './saluteLinee';
import './phases.css';

/* ── Le 4 fasi dell'operazione ── */
const STEPS = [
  { key: 'avvio', label: 'Avvio' },
  { key: 'registrazione', label: 'Registrazione' },
  { key: 'trascrizione', label: 'Trascrizione' },
  { key: 'analisi', label: 'Risultato' },
];

const FASE_LABEL = {
  avvio: 'Avvio chiamata',
  registrazione: 'Risposta sistema automatico',
  trascrizione: 'Trascrizione',
  analisi: 'Risultato',
  attesa: 'In attesa',
  standby: 'Standby — disservizio 456',
};

/* PhaseAnimation è in ./PhaseAnimation.js (condiviso con la sidebar) */

/* ── Tracker delle 4 fasi ── */
function StepTracker({ fase }) {
  const activeIdx = STEPS.findIndex(s => s.key === fase); // -1 per attesa/standby
  return (
    <div className="ph-steps">
      {STEPS.map((s, i) => {
        const done = activeIdx > i;
        const active = activeIdx === i;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <div className={`ph-conn${activeIdx >= i ? ' done' : ''}`} />}
            <div className={`ph-step${active ? ' active' : ''}${done ? ' done' : ''}`}>
              <span className="dot" />
              <span className="nm">{s.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* Sintesi statistica (non analitica) degli ultimi esiti + trend della resa NON TIM */
function RecentStats({ ultimi, ts }) {
  const n = ultimi.length;
  const isNT = x => x === 'NON TIM';
  const nNT = ultimi.filter(isNT).length;
  const nT = ultimi.filter(x => x === 'TIM').length;
  const nE = n - nNT - nT;
  const pct = c => (n ? Math.round((c / n) * 100) : 0);

  // Trend: resa NON TIM nella prima metà vs seconda metà degli ultimi esiti
  const h = Math.floor(n / 2);
  const ratio = arr => (arr.length ? arr.filter(isNT).length / arr.length : 0);
  const d = ratio(ultimi.slice(h)) - ratio(ultimi.slice(0, h));
  const trend = n < 4 ? null : d > 0.1 ? 'up' : d < -0.1 ? 'down' : 'flat';
  const TT = { up: '▲ resa in aumento', down: '▼ resa in calo', flat: '▬ resa stabile' };
  const TC = { up: '#4ade80', down: '#f87171', flat: '#cbd5e1' };

  // Velocità dai timestamp degli ultimi esiti + stima su 24h
  const t = ts || [];
  let perOra = null, in24h = null;
  if (t.length >= 2) {
    const span = t[t.length - 1] - t[0]; // secondi
    if (span > 0) { perOra = Math.round(((t.length - 1) / span) * 3600); in24h = perOra * 24; }
  }

  return (
    <div className="ph-stats">
      <div className="ph-stats-head">
        <span>Ultimi {n} esiti</span>
        {trend && <span style={{ color: TC[trend] }}>{TT[trend]}</span>}
      </div>
      <div className="ph-stack">
        <span style={{ width: `${pct(nNT)}%`, background: '#22c55e' }} />
        <span style={{ width: `${pct(nT)}%`, background: '#ef4444' }} />
        <span style={{ width: `${pct(nE)}%`, background: '#f59e0b' }} />
      </div>
      <div className="ph-legend">
        <span><i style={{ background: '#22c55e' }} />NON TIM {nNT} · {pct(nNT)}%</span>
        <span><i style={{ background: '#ef4444' }} />TIM {nT} · {pct(nT)}%</span>
        <span><i style={{ background: '#f59e0b' }} />Err {nE} · {pct(nE)}%</span>
      </div>
      {perOra != null && (
        <div className="ph-speed">
          <span>Velocità <b>~{perOra.toLocaleString('it-IT')}</b>/ora</span>
          <span>Stima 24h <b>~{in24h.toLocaleString('it-IT')}</b></span>
        </div>
      )}
    </div>
  );
}

/* ── Salute delle linee SIP ──
   Sempre presente, anche a riposo: e' la pagina dove si vede se una linea sta cadendo.
   Il 30/09/2026 una linea accesa ma muta ha fatto fallire dieci verifiche di fila e non
   c'era nessun posto in cui accorgersene. */
function SaluteLinee({ dati }) {
  const linee = dati?.linee || [];
  if (!linee.length) return null;
  return (
    <div style={{ marginBottom: 12, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 12, padding: '11px 13px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 9 }}>
        <span style={{ fontWeight: 800, fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--accent)' }}>Linee telefoniche</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>ultimi {dati.giorni} giorni</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {linee.map((l) => (
          <div key={l.linea}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: colore(l.stato), flex: '0 0 auto' }} />
              <span style={{ fontWeight: 700, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                             opacity: l.accesa ? 1 : 0.55 }}>
                {l.linea}{l.accesa ? '' : ' (spenta)'}
              </span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,.65)' }}>{etichetta(l.stato)}</span>
            </div>
            <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,.10)', margin: '6px 0 4px 17px', overflow: 'hidden' }}>
              <div style={{ width: `${l.tentativi ? l.percentuale : 0}%`, height: '100%', background: colore(l.stato) }} />
            </div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', marginLeft: 17 }}>
              {conteggio(l)}{l.tentativi ? ` · ultimo esito ${quando(l.ultimo_ok)}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StatsDialog({ open, onClose }) {
  const [job, setJob] = useState(null);
  const [salute, setSalute] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [closing, setClosing] = useState(false);
  const [tab, setTab] = useState('riepilogo');
  const [raggiunti, setRaggiunti] = useState([]);

  const loadRaggiunti = () => axios.get(`${API}/obiettivi`)
    .then((r) => setRaggiunti((r.data || []).filter((o) => o.stato === 'raggiunto')))
    .catch(() => {});

  const rimuoviObiettivo = async (id) => {
    try { await axios.delete(`${API}/obiettivi/${id}`); loadRaggiunti(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    setConfirmCancel(false);
    loadRaggiunti();
    // la salute delle linee cambia lentamente: si legge all'apertura, non a ogni giro
    axios.get(`${API}/456/salute-linee`).then((r) => setSalute(r.data)).catch(() => {});
    const load = () => {
      axios.get(`${API}/active-job`)
        .then(r => setJob(r.data?.job))
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 1500);
    return () => clearInterval(id);
  }, [open]);  // eslint-disable-line

  const handleCancel = async () => {
    if (!job?.id) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/cancel`);
      toast.info("Annullamento richiesto — si fermera' a breve");
    } catch {
      toast.error('Errore annullamento');
    }
    setCancelling(false);
    setConfirmCancel(false);
  };

  if (!open) return null;

  const standby = job && job.status === 'standby';
  const fase = standby ? 'standby' : (job?.fase || 'avvio');

  // Statistiche per LINEA (aggregano gli interni della stessa linea)
  const perLine = Array.isArray(job?.per_line) ? job.per_line : [];
  const activeLineIdx = tab.startsWith('l') ? Number(tab.slice(1)) : -1;
  const activeLine = activeLineIdx >= 0 ? perLine[activeLineIdx] : null;
  const showRiepilogo = tab === 'riepilogo' || !activeLine;

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setTab(id)}
      style={{
        padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        border: 'none', background: 'none',
        color: tab === id ? '#fff' : 'rgba(255,255,255,0.55)',
        borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
      }}
    >{label}</button>
  );

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">

        {/* Tech / TLC animated background */}
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {job && job.status === 'running' && (
            <>
              <span className="bulk-stream s0" /><span className="bulk-stream s1" />
              <span className="bulk-stream s2" /><span className="bulk-stream s3" />
            </>
          )}
          {job && job.status === 'running' && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        {/* Foreground content */}
        <div className="bulk-content">
          <div className="bulk-head">
            <span className="bulk-title">Operazione in corso</span>
            <button className="bulk-x" onClick={handleClose}><IconClose size={16} color="#fff" /></button>
          </div>

          {raggiunti.length > 0 && (
            <div style={{ marginBottom: 12, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.10)', borderRadius: 12, padding: '11px 13px' }}>
              <div style={{ fontWeight: 800, fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 9 }}>Obiettivi raggiunti</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {raggiunti.map((o) => (
                  <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 9, background: 'rgba(34,197,94,.18)', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
                      <IconCheck size={15} color="#4ade80" />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.nome}</div>
                      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)' }}>
                        {o.verifica_completa ? 'verifica completa terminata' : `target ${Number(o.target).toLocaleString('it-IT')} raggiunto`}
                      </div>
                    </div>
                    <button onClick={() => rimuoviObiettivo(o.id)}
                      style={{ flex: '0 0 auto', width: 'auto', padding: '6px 13px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: '1px solid rgba(239,68,68,.45)', background: 'rgba(239,68,68,.15)', color: '#fca5a5', cursor: 'pointer' }}>
                      Rimuovi
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <SaluteLinee dati={salute} />

          {!job ? (
            <div className="bulk-empty">
              <div className="bulk-empty-ico">✓</div>
              <p>Nessuna operazione in corso</p>
            </div>
          ) : (
            <div className="ph-wrap">
              {perLine.length > 0 && (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                  {tabBtn('riepilogo', 'Riepilogo')}
                  {perLine.map((l, i) => tabBtn(`l${i}`, String(i + 1)))}
                </div>
              )}

              {showRiepilogo ? (
                <>
                  <Agente456Anim fase={fase} ultimo={job.ultimi && job.ultimi[job.ultimi.length - 1]} />
                  <div className="ph-label">{FASE_LABEL[fase] || 'In lavorazione'}</div>
                  <div className="ph-sub">
                    {job.current_number
                      ? <>numero in lavorazione: <span className="ph-num">{job.current_number}</span></>
                      : 'preparazione…'}
                  </div>

                  {!standby && <StepTracker fase={fase} />}

                  {job.ultimi && job.ultimi.length > 0 && <RecentStats ultimi={job.ultimi} ts={job.ts} />}
                </>
              ) : (
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', padding: '8px 4px', lineHeight: 1.9 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: (activeLine.registrati || 0) > 0 ? '#22c55e' : '#ef4444' }} />
                    <strong style={{ fontSize: 15 }}>{activeLine.nome || activeLine.server}</strong>
                    <span style={{ opacity: 0.7 }}>({activeLine.registrati}/{activeLine.totali} interni registrati)</span>
                  </div>
                  <div>Sto chiamando: <span className="ph-num">{activeLine.numero_corrente || '—'}</span></div>
                  <div>
                    Completati: <b>{activeLine.completati || 0}</b>
                    <span style={{ marginLeft: 12 }}>TIM: {activeLine.tim || 0}</span>
                    <span style={{ marginLeft: 12 }}>NON TIM: {activeLine.non_tim || 0}</span>
                    <span style={{ marginLeft: 12 }}>errori: {activeLine.errori || 0}</span>
                  </div>
                  <div style={{ opacity: 0.75 }}>Velocità: {activeLine.velocita_min != null ? `${activeLine.velocita_min}/min` : '—'}</div>
                </div>
              )}

              {job.status === 'running' && (
                confirmCancel ? (
                  <div className="bulk-confirm">
                    <span className="bulk-confirm-q">Annullare l'operazione in corso?</span>
                    <div className="bulk-confirm-btns">
                      <button className="bulk-cancel" onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? '…' : 'Sì'}
                      </button>
                      <button className="bulk-keep" onClick={() => setConfirmCancel(false)} disabled={cancelling}>
                        No
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="bulk-cancel" onClick={() => setConfirmCancel(true)}>
                    Annulla operazione
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
